<template>
  <div class="w-screen h-screen flex flex-col items-center justify-center overflow-hidden" style="background-color: #020308">
    <!-- PASSWORD RESET SUCCESSFUL -->
    <div v-if="resetSuccessful" class="flex flex-col items-center w-full pb-10 fade-in" style="max-width: 360px">
      <div class="flex items-center justify-center w-11 h-11 rounded-xl border-2 border-white">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M7 13L10 16L17 8" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <BaseText type="heading" size="md" class="text-white mt-4">
        Password Reset Successful
      </BaseText>
      <BaseText type="body" size="sm" class="text-neutral-alpha-600 mt-2 text-center">
        You can close this page or
      </BaseText>
      <router-link to="/login" replace class="submit-button mt-8 px-4 py-2 h-10 bg-white flex items-center justify-center transition duration-200 hover:bg-opacity-90">
        <BaseText type="heading" size="sm">
          Continue to Sign In
        </BaseText>
      </router-link>
    </div>
    <!-- EMAIL SENT -->
    <div v-else-if="emailSent" class="flex flex-col items-center fade-in">
      <img src="../../assets/images/foreplay-logo.png" class="mb-12" style="width: 136px; height: 40px;" />
      <EmailSentDirections 
        :to-email="email"
        subheading="We sent you a password reset link. If you don't see it, please check your spam folder." 
        resend-text="link"
        @resend="sendEmailLink"
      />
    </div>
    <!-- FORM -->
    <div v-else class="flex flex-col items-center w-full pb-10" style="max-width: 400px;">
      <!-- Logo -->
      <img src="../../assets/images/foreplay-logo.png" style="width: 136px; height: 40px;" />
      <!-- Copy -->
      <BaseText type="heading" size="md" class="text-white mt-12">
        Forgot your password?
      </BaseText>
      <BaseText type="body" size="sm" class="text-neutral-alpha-650 mt-2 text-center" style="max-width: 350px;">
        No worries, we all do. Please enter your email address so we can send you a reset link.
      </BaseText>
      <!-- Form -->
      <form class="w-full mt-12" @submit.prevent="checkForm">
        <!-- Email Input -->
        <BaseText type="label" size="md" class="text-white w-full mb-1">
          Email
        </BaseText>
        <div class="input-container w-full flex items-center" :class="{'active': email?.length || emailInputFocused, 'errored': emailError && !emailInputFocused}">
          <input v-model="email" class="form-input flex-grow min-w-0" placeholder="Enter your email address" type="email"
          @focus="emailInputFocused = true" @blur="emailInputFocused = false" @input="() => {submitAttempted = false; requestError = null}" />
          <transition>
            <InfoIcon v-if="!emailInputFocused && emailError" style="color: #DF1C41" />
          </transition>
        </div>
        <transition name="error">
          <div v-if="emailError" class="w-full flex items-end h-7">
            <BaseText type="body" size="sm" class="transition-colors" :style="{ color: emailInputFocused ? 'rgba(255, 255, 255, 0.68)' : '#DF1C41' }">
              {{ emailError }}
            </BaseText>
          </div>
        </transition>
        <!-- Send Reset Link Button -->
        <button class="submit-button w-full mt-6 p-2 h-10 flex items-center justify-center transition duration-200"
        :class="submitDisabled ? 'disabled' : 'bg-white bg-opacity-100 hover:bg-opacity-90'"
        type="submit" :disabled="loadingEmailLink" :loading="loadingEmailLink">
          <BaseLoadingSpinnerCircle v-if="loadingEmailLink" :duration="1" />
          <BaseText v-else type="heading" size="sm">
            Send Reset Link
          </BaseText>
        </button>
      </form>
      <!-- Invalid Email Error Message -->
      <transition name="request-error">
        <div v-if="requestError" class="flex items-end" style="height: 68px;">
          <div class="w-full flex items-center gap-2 p-2.5 rounded-lg bg-neutral-alpha-10">
            <InfoIcon class="text-neutral-alpha-650 flex-shrink-0" :width="24" :height="24" />
            <BaseText type="body" size="sm" class="text-neutral-alpha-650">
              {{ 
                requestError === 'no-user' ? 'No user found with this email address.' : ''
              }}
            </BaseText>
          </div>
        </div>
      </transition>
      <!-- Back to Login -->
      <div class="w-full flex gap-1.5 items-center justify-center mt-12">
        <BaseText type="body" size="sm" class="text-neutral-alpha-650">
          Remembered it?
        </BaseText>
        <router-link to="/login" class="text-neutral-alpha-700 transition-colors duration-100 hover:text-white">
          <BaseText type="label" size="sm">
            Go back to sign in
          </BaseText>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from '@/api/config/FirebaseConfig'
import FunctionsAPI from '@/api'

import EmailSentDirections from '../../components/account/EmailSentDirections.vue'
import InfoIcon from '../../components/globals/Icons/InfoSimpleIcon.vue'

export default {
  name: 'ForgotPasswordView',
  components: {
    EmailSentDirections,
    InfoIcon
  },
  data () {
    return {
      email: null,
      
      loadingEmailLink: false,
      emailSent: false,
      resetSuccessful: false,
      
      emailInputFocused: false,
      submitAttempted: false,
      requestError: null
    }
  },
  created () {
    // Listen for password reset status
    this.bc = new BroadcastChannel('password-reset-status')
    this.bc.onmessage = (event) => {
      const { isPasswordReset } = event.data
      this.resetSuccessful = isPasswordReset
    }
  },
  computed: {
    submitDisabled () {
      return this.loadingEmailLink || !this.email?.length || this.emailError
    },
    emailError () {
      if (!this.email?.length) {
        return this.submitAttempted ? 'Email required.' : null
      }
      const emailRegex = /^[^\s@]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;
      if (!emailRegex.test(this.email) || this.emailIsDisposable) return 'Please enter a valid email address.'

      return null
    },
  },
  methods: {
    async checkForm (e) {
      this.submitAttempted = true
      if (!this.submitDisabled) {
        await this.sendEmailLink()
      }

      e.preventDefault()
    },
    async sendEmailLink () {
      this.loadingEmailLink = true
      try {
        // Check that a user with this email exists
        const signInMethods = await firebase.auth().fetchSignInMethodsForEmail(this.email)
        if (!signInMethods?.length) {
          this.requestError = 'no-user'
          return
        }

        await FunctionsAPI.Transactions.sendPasswordReset(this.email)
        this.emailSent = true
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: error
        })
      } finally {
        this.loadingEmailLink = false
      }
    }
  }
}
</script>

<style scoped>
.submit-button {
  background-color: rgba(255, 255, 255, 1);
  color: #13151A;
  border-radius: 10px;
  box-shadow: none;
  transition: background-color 150ms ease-in-out, color 150ms ease-in-out, box-shadow 150ms ease-in-out;
}
.submit-button:hover {
  background-color: rgba(255, 255, 255, 0.9);
}
.submit-button.disabled, .submit-button.disabled:hover {
  background-color: transparent;
  color: rgba(255, 255, 255, 0.32);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1);
  cursor: default;
}

.input-container {
  background-color: rgba(255, 255, 255, 0.10);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.10);
  border-radius: 10px;
  padding: 8px 14px;
  display: flex;
  align-items: center;
  transition: background-color 150ms ease-in-out, box-shadow 150ms ease-in-out;
}
.input-container:hover {
  background-color: rgba(255, 255, 255, 0.16);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.16);
}
.input-container.active, .input-container.active:hover {
  background-color: transparent;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.10);
}
.input-container.errored, .input-container.active.errored, .input-container.active.errored:hover {
  background-color: transparent;
  box-shadow: 0 0 0 1px #F4003A;
}

.form-input {
  background-color: transparent;
  color: white;
  caret-color: white;
  max-width: none;
  font-weight: 300;
  font-size: 16px;
  line-height: 24px;

  /* Reset default styles */
  -webkit-appearance: none;  /* Remove default styling in WebKit browsers */
  -moz-appearance: none;     /* Remove default styling in Firefox */
  appearance: none;          /* Remove default styling in modern browsers */
  padding: 0;
  margin: 0;
  border: none;
  font-size: inherit;
  outline: none;

  /* This is because browsers want to force their ugly styling on you when autofill is triggered. */
  /* This is the only way to prevent that, since they use !important in their overrides. */
  transition:
    color 99999999s step-end,
    background-color 99999999s step-end;
}
.form-input:focus {
  outline: none;
  border: none;
  box-shadow: none;
}
.form-input::placeholder {
  transition: color 200ms ease-in-out;
  color: rgba(255, 255, 255, 0.44);
}
.form-input:focus::placeholder {
  color: rgba(255, 255, 255, 0.2);
}

/* Override browser autofill styles */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
textarea:-webkit-autofill,
textarea:-webkit-autofill:hover,
textarea:-webkit-autofill:focus,
select:-webkit-autofill,
select:-webkit-autofill:hover,
select:-webkit-autofill:focus {
  -webkit-text-fill-color: white !important;
}

/* Firefox autocomplete */
.form-input:-moz-autofill {
  color: white !important;
}

/* General autocomplete prevention */
.form-input:autofill {
  color: white !important;
}

.v-enter-active, .v-leave-active {
  transition: opacity 75ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
.error-enter-active {
  animation: errorEnter 75ms ease-in-out forwards;
}
.error-leave-active {
  animation: errorLeave 75ms ease-in-out forwards;
}
@keyframes errorEnter {
  0% { height: 0px; opacity: 0; }
  50% { opacity: 0; }
  100% { height: 28px; opacity: 1; }
}
@keyframes errorLeave {
  0% { height: 28px; opacity: 1; }
  50% { opacity: 0; }
  100% { height: 0px; opacity: 0; }
}

.fade-in {
  animation: fadeIn 150ms ease-in-out forwards;
}
@keyframes fadeIn {
  0% { opacity: 0; }
  100% { opacity: 1; }
}

.request-error-enter-active {
  animation: requestErrorEnter 150ms ease-in-out forwards;
}
.request-error-leave-active {
  animation: requestErrorLeave 150ms ease-in-out forwards;
}
@keyframes requestErrorEnter {
  0% { height: 0px; opacity: 0; }
  50% { height: 68px; opacity: 0; }
  100% { height: 68px; opacity: 1; }
}
@keyframes requestErrorLeave {
  0% { height: 68px; opacity: 1; }
  50% { height: 0px; opacity: 0; }
  100% { height: 0px; opacity: 0; }
}
</style>